import { ref, type ComputedRef } from 'vue';
import { CREATE_SENDBIRD_CHANNEL } from '@/graphql/messages/mutations';
import { CREATE_JOB_RECRUIT } from '@/graphql/recruit_sitter/mutation';
import { readMeta } from '@/lib/page';
import { setRecentlyViewedProviderId } from '@/lib/recentlyViewedProfile';
import type { SuccessPayload } from './types';
import type { RecruitApollo } from './useRecruitJobs';

type RecruitSitterOptions = {
  emitSuccess?: boolean;
};

export function useRecruitFlow(options: {
  recipientUuid: ComputedRef<string>;
  isBasic: ComputedRef<boolean>;
  selectedJobUuid: ComputedRef<string | null>;
  getClient: () => RecruitApollo;
  onSuccess: (payload: SuccessPayload) => void;
}) {
  const messageSendLoading = ref(false);
  const messageSent = ref(false);
  const showFeatureTable = ref(false);
  const showErrorBanner = ref(false);
  const errorMessage = ref('');

  function shouldRecruitForSelectedJob(): boolean {
    return Boolean(options.selectedJobUuid.value);
  }

  function shouldSendMessageAfterRecruitFlow(note: string): boolean {
    return note.length > 0 && (!shouldRecruitForSelectedJob() || !showFeatureTable.value);
  }

  function notifySuccess(recruited: boolean, sentMessage: boolean): void {
    options.onSuccess({
      recipientUuid: options.recipientUuid.value,
      recruited,
      sentMessage,
    });
  }

  function rememberViewedProvider(): void {
    if (options.isBasic.value) {
      setRecentlyViewedProviderId(options.recipientUuid.value);
    }
  }

  function errorCode(error: unknown): string | undefined {
    const graphQLErrors = (error as { graphQLErrors?: Array<{ extensions?: { error_code?: string } }> }).graphQLErrors;
    return graphQLErrors?.[0]?.extensions?.error_code;
  }

  async function runSendFlow(note: string = ''): Promise<void> {
    const normalizedMessage = typeof note === 'string' ? note : '';
    showErrorBanner.value = false;
    messageSendLoading.value = true;
    try {
      const recruited = shouldRecruitForSelectedJob() ? await recruitSitter() : false;
      const sentMessage = shouldSendMessageAfterRecruitFlow(normalizedMessage)
        ? await createSendbirdChannel(normalizedMessage)
        : false;
      if (!recruited && !sentMessage) {
        return;
      }

      notifySuccess(recruited, sentMessage);
      messageSent.value = true;
    } catch (_error) {
      showErrorBanner.value = true;
      errorMessage.value = 'There was an error sending your message. Refresh and try again.';
    } finally {
      messageSendLoading.value = false;
    }
  }

  async function recruitSitter(recruitOptions: RecruitSitterOptions = {}): Promise<boolean> {
    const { emitSuccess = false } = recruitOptions;
    try {
      await createJobRecruit();
      if (emitSuccess) {
        notifySuccess(true, false);
      }
      return true;
    } catch (error: unknown) {
      if (errorCode(error) === 'max_recruit_error') {
        showFeatureTable.value = true;
        return false;
      }
      showErrorBanner.value = true;
      errorMessage.value = 'There was an error sending your message. Refresh and try again.';
      throw error;
    }
  }

  async function createSendbirdChannel(note: string): Promise<boolean> {
    try {
      await options.getClient().mutate({
        mutation: CREATE_SENDBIRD_CHANNEL,
        variables: {
          accountId: options.recipientUuid.value,
          message: note,
          session: readMeta('session'),
        },
      });
      messageSent.value = true;
      return true;
    } catch (error: unknown) {
      showErrorBanner.value = true;
      errorMessage.value = 'There was an error sending your message. Try again later.';
      throw error;
    }
  }

  async function createJobRecruit(): Promise<void> {
    if (!options.selectedJobUuid.value) {
      return;
    }
    await options.getClient().mutate({
      mutation: CREATE_JOB_RECRUIT,
      variables: {
        jobId: options.selectedJobUuid.value,
        providerId: options.recipientUuid.value,
      },
    });
  }

  return {
    messageSendLoading,
    messageSent,
    showFeatureTable,
    showErrorBanner,
    errorMessage,
    runSendFlow,
    rememberViewedProvider,
  };
}
